// src/pages/admin/AdminInscricoesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2'; // Importar Swal
import { getAdminInscricoes, getEventos, updateInscricaoStatus, confirmarPendentesEmMassa } from '../../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Label } from '../../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/Card';
import { Skeleton } from '../../components/ui/Skeleton';
import { Button } from '../../components/ui/Button'; // Importar Button
import { CheckCircle, Loader2, Info, Building2, GraduationCap, MapPin, Clock } from 'lucide-react'; // Importar MapPin e Clock
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/Popover'; // Importar Popover

const StatusInscricao = {
    PENDENTE: 'PENDENTE',
    CONFIRMADA: 'CONFIRMADA',
    CANCELADA: 'CANCELADA',
};

const statusMap = {
    [StatusInscricao.PENDENTE]: 'Pendente',
    [StatusInscricao.CONFIRMADA]: 'Confirmada',
    [StatusInscricao.CANCELADA]: 'Cancelada',
};

const getStatusClass = (status) => {
    switch (status) {
        case StatusInscricao.CONFIRMADA:
            return 'bg-green-100 text-green-800';
        case StatusInscricao.PENDENTE:
            return 'bg-yellow-100 text-yellow-800';
        case StatusInscricao.CANCELADA:
            return 'bg-red-100 text-red-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

function AdminInscricoesPage() {
    const [inscricoes, setInscricoes] = useState([]);
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isConfirming, setIsConfirming] = useState(false);

    const [filtroEventoId, setFiltroEventoId] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('');

    const fetchInscricoes = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const filters = {};
            if (filtroEventoId) filters.eventoId = filtroEventoId;
            if (filtroStatus) filters.status = filtroStatus;
            
            const data = await getAdminInscricoes(filters);
            setInscricoes(data);
        } catch (err) {
            console.error('Erro ao buscar inscrições:', err);
            setError('Não foi possível carregar as inscrições. ' + err.message);
            toast.error('Não foi possível carregar as inscrições.');
        } finally {
            setLoading(false);
        }
    }, [filtroEventoId, filtroStatus]);

    const fetchEventos = useCallback(async () => {
        try {
            // Busca todos os eventos para o filtro
            const data = await getEventos();
            setEventos(data);
        } catch (err) {
            console.error('Erro ao buscar eventos para filtro:', err);
        }
    }, []);

    useEffect(() => {
        fetchEventos();
    }, [fetchEventos]);

    useEffect(() => {
        fetchInscricoes();
    }, [fetchInscricoes]);

    const handleStatusChange = async (inscricaoId, novoStatus) => {
        try {
            await updateInscricaoStatus(inscricaoId, novoStatus);
            toast.success('Status da inscrição atualizado com sucesso!');
            fetchInscricoes();
        } catch (err) {
            console.error('Erro ao alterar status:', err);
            toast.error('Erro ao alterar status: ' + err.message);
        }
    };

    const handleConfirmarEmMassa = async () => {
        const eventoNome = filtroEventoId 
            ? eventos.find(e => String(e.id) === filtroEventoId)?.nome 
            : 'toda a plataforma';

        const result = await Swal.fire({
            title: 'Confirmar Inscrições Pendentes?',
            text: `Você tem certeza que deseja confirmar TODAS as inscrições PENDENTES ${filtroEventoId ? `para o evento "${eventoNome}"` : 'em toda a plataforma'}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, Confirmar!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            setIsConfirming(true);
            try {
                const response = await confirmarPendentesEmMassa(filtroEventoId);
                toast.success(response.message);
                // Se o filtro de status atual for 'PENDENTE' ou 'todos', recarregamos a lista
                if (filtroStatus === StatusInscricao.PENDENTE || filtroStatus === '') {
                    fetchInscricoes();
                }
            } catch (err) {
                console.error('Erro ao confirmar em massa:', err);
                toast.error(err.message || 'Erro ao confirmar inscrições em massa.');
            } finally {
                setIsConfirming(false);
            }
        }
    };

    const InscricoesTableSkeleton = () => (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-left">
                    <tr className="border-b">
                        <th className="p-4 font-medium">Participante</th>
                        <th className="p-4 font-medium hidden md:table-cell">Evento</th>
                        <th className="p-4 font-medium hidden lg:table-cell">Data</th>
                        <th className="p-4 font-medium">Status</th>
                        <th className="p-4 font-medium text-right">Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: 8 }).map((_, index) => (
                        <tr key={index} className="border-b">
                            <td className="p-4">
                                <Skeleton className="h-5 w-3/4" />
                                <Skeleton className="h-4 w-full mt-2 hidden sm:block" />
                            </td>
                            <td className="p-4 hidden md:table-cell"><Skeleton className="h-5 w-2/4" /></td>
                            <td className="p-4 hidden lg:table-cell"><Skeleton className="h-5 w-20" /></td>
                            <td className="p-4"><Skeleton className="h-6 w-24 rounded-full" /></td>
                            <td className="p-4 text-right">
                                <Skeleton className="h-8 w-[120px] ml-auto" />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    if (error) {
        return <div className="container mx-auto p-4 md:p-6 lg:p-8 text-destructive">{error}</div>;
    }

    // Verifica se há inscrições pendentes visíveis na lista atual
    const hasPendingInscricoes = inscricoes.some(i => i.status === StatusInscricao.PENDENTE);

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            {/* Ajuste: Usar items-center para alinhar verticalmente o título e o botão */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Gerenciamento de Inscrições</h1>
                    <p className="text-muted-foreground">Filtre e gerencie todas as inscrições dos seus eventos.</p>
                </div>
                {/* Botão de Confirmação em Massa */}
                <Button 
                    onClick={handleConfirmarEmMassa} 
                    disabled={isConfirming || loading || !hasPendingInscricoes}
                    className="flex-shrink-0"
                >
                    {isConfirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                    {isConfirming ? 'A confirmar...' : 'Confirmar Pendentes'}
                </Button>
            </div>

            <Card className="mb-6">
                <CardHeader>
                    <CardTitle className="mb-2">Filtros</CardTitle>
                    <CardDescription className="leading-relaxed">Refine a sua busca por evento ou status da inscrição.</CardDescription>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4">
                    <div className="grid gap-2">
                        <Label htmlFor="filterEvento" className="mb-1">Filtrar por Evento:</Label>
                        <Select onValueChange={(value) => setFiltroEventoId(value === 'todos' ? '' : value)} value={filtroEventoId || 'todos'}>
                            <SelectTrigger id="filterEvento" className="w-full overflow-hidden">
                                <SelectValue placeholder="Todos os Eventos" className="w-full overflow-hidden whitespace-nowrap text-ellipsis" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="todos" className="w-full overflow-hidden whitespace-nowrap text-ellipsis">Todos os Eventos</SelectItem>
                                {eventos.map(evento => (
                                    <SelectItem key={evento.id} value={String(evento.id)} className="w-full overflow-hidden whitespace-nowrap text-ellipsis">
                                        {evento.nome}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="filterStatus" className="mb-1">Filtrar por Status:</Label>
                        <Select onValueChange={(value) => setFiltroStatus(value === 'todos' ? '' : value)} value={filtroStatus || 'todos'}>
                            <SelectTrigger id="filterStatus">
                                <SelectValue placeholder="Todos os Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="todos">Todos os Status</SelectItem>
                                {Object.values(StatusInscricao).map(status => (
                                    <SelectItem key={status} value={status}>{statusMap[status]}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="mb-2">Lista de Inscrições</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <InscricoesTableSkeleton />
                    ) : inscricoes.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">Nenhuma inscrição encontrada com os filtros aplicados.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left">
                                    <tr className="border-b">
                                        <th className="p-4 font-medium">Participante</th>
                                        <th className="p-4 font-medium hidden md:table-cell">Evento</th>
                                        <th className="p-4 font-medium hidden lg:table-cell">Data</th>
                                        <th className="p-4 font-medium">Status</th>
                                        <th className="p-4 font-medium text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inscricoes.map(inscricao => (
                                        <tr key={inscricao.id} className="border-b hover:bg-accent/50 even:bg-muted/50 transition-colors">
                                            <td className="p-4 font-medium">
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <span className="cursor-pointer hover:text-primary transition-colors flex items-center">
                                                            {inscricao.nomeParticipante}
                                                            <Info className="ml-2 h-3 w-3 text-muted-foreground" />
                                                        </span>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-80 p-4 space-y-2 text-sm">
                                                        <h4 className="font-semibold mb-2 border-b pb-1 text-primary">Detalhes da Inscrição</h4>
                                                        
                                                        {/* Detalhes do Participante */}
                                                        <div className="space-y-1 border-b pb-2">
                                                            <p className="font-medium text-foreground">Participante:</p>
                                                            <div className="flex items-center text-muted-foreground">
                                                                <GraduationCap className="mr-2 h-4 w-4" />
                                                                <p><strong>Grau Acadêmico:</strong> {inscricao.grauAcademicoParticipante || 'N/A'}</p>
                                                            </div>
                                                            <div className="flex items-center text-muted-foreground">
                                                                <Building2 className="mr-2 h-4 w-4" />
                                                                <p><strong>Instituição:</strong> {inscricao.instituicaoParticipante || 'N/A'}</p>
                                                            </div>
                                                        </div>

                                                        {/* Detalhes do Evento */}
                                                        <div className="space-y-1 pt-2">
                                                            <p className="font-medium text-foreground">Evento:</p>
                                                            <div className="flex items-center text-muted-foreground">
                                                                <MapPin className="mr-2 h-4 w-4" />
                                                                <p><strong>Local:</strong> {inscricao.localEvento || 'N/A'}</p>
                                                            </div>
                                                            <div className="flex items-center text-muted-foreground">
                                                                <Clock className="mr-2 h-4 w-4" />
                                                                <p><strong>Carga Horária:</strong> {inscricao.cargaHorariaTotalEvento || 'N/A'}h</p>
                                                            </div>
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                                <div className="text-muted-foreground text-xs hidden sm:block">{inscricao.emailParticipante}</div>
                                            </td>
                                            <td className="p-4 text-muted-foreground hidden md:table-cell">{inscricao.nomeEvento}</td>
                                            <td className="p-4 text-muted-foreground hidden lg:table-cell">{new Date(inscricao.dataInscricao).toLocaleDateString()}</td>
                                            <td className="p-4">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClass(inscricao.status)}`}>
                                                    {statusMap[inscricao.status] || inscricao.status}
                                                </span>
                                            </td>
                                            <td className="p-4 text-right">
                                                <Select
                                                    value={inscricao.status}
                                                    onValueChange={(novoStatus) => handleStatusChange(inscricao.id, novoStatus)}
                                                >
                                                    <SelectTrigger className="h-8 w-[120px] ml-auto">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {Object.values(StatusInscricao).map(status => (
                                                            <SelectItem key={status} value={status}>{statusMap[status]}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default AdminInscricoesPage;